import React, { useMemo } from "react";
import {
  Calendar, CalendarDays, Clock, TrendingUp, AlertTriangle, BarChart3,
  CheckCircle, X, Users, Bell, RefreshCw, Car,
} from "lucide-react";
import { StatCard, SectionCard, EmptyState, StatusBadge, APPOINTMENT_STATUS } from "./AdminUI";

const StatisticsTab = ({ ctx }) => {
  const { statistics, statsLoading, handleSendReminders, sendingReminders, fetchStatistics } = ctx;

  if (statsLoading) {
    return <EmptyState icon={Clock} title="Chargement des statistiques..." />;
  }
  if (!statistics) {
    return <EmptyState icon={AlertTriangle} title="Impossible de charger les statistiques" />;
  }

  const rate = statistics.cancellation_rate || 0;
  const rateColor = rate > 20 ? "text-red-400" : rate > 10 ? "text-amber-400" : "text-emerald-400";
  const rateBar = rate > 20 ? "bg-red-500" : rate > 10 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <BarChart3 size={20} className="text-[#FF3B30]" />
            Tableau de bord
          </h2>
          <p className="text-white/50 text-xs mt-0.5">Vue synthétique de votre activité</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSendReminders}
            disabled={sendingReminders}
            className="bg-[#FF3B30] hover:bg-[#D63025] disabled:bg-[#FF3B30]/50 text-white px-3.5 py-2 rounded-md text-sm font-semibold flex items-center gap-2 transition-colors"
          >
            <Bell size={14} />
            {sendingReminders ? "Envoi..." : "Rappels demain"}
          </button>
          <button
            onClick={fetchStatistics}
            className="bg-[#1C1C1E] hover:bg-white/[0.08] text-white px-3.5 py-2 rounded-md text-sm flex items-center gap-2 border border-white/[0.06] transition-colors"
          >
            <RefreshCw size={14} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Main stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={CalendarDays} label="Total RDV" value={statistics.total} color="text-white" accent="bg-white/[0.06]" />
        <StatCard icon={Clock} label="Aujourd'hui" value={statistics.today} color="text-emerald-400" accent="bg-emerald-500/10" />
        <StatCard icon={TrendingUp} label="Cette semaine" value={statistics.this_week} color="text-blue-400" accent="bg-blue-500/10" />
        <StatCard icon={Calendar} label="Ce mois" value={statistics.this_month} color="text-purple-400" accent="bg-purple-500/10" />
      </div>

      {/* Secondary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Clock} label="En attente" value={statistics.pending} color="text-amber-400" accent="bg-amber-500/10" />
        <StatCard icon={CheckCircle} label="Confirmés" value={statistics.confirmed} color="text-emerald-400" accent="bg-emerald-500/10" />
        <StatCard icon={X} label="Annulés" value={statistics.cancelled} color="text-red-400" accent="bg-red-500/10" />
        <StatCard icon={Users} label="Clients uniques" value={statistics.unique_clients} color="text-cyan-400" accent="bg-cyan-500/10" />
      </div>

      {/* Cancellation + Popular Services */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Taux d'annulation" icon={AlertTriangle} iconColor="text-orange-400">
          <div className="flex items-end gap-3">
            <p className={`text-4xl font-bold ${rateColor}`}>{rate}%</p>
            <p className="text-white/40 text-xs mb-1.5">
              {rate <= 10 ? "Excellent" : rate <= 20 ? "Acceptable" : "À surveiller"}
            </p>
          </div>
          <div className="mt-3 h-2 bg-white/[0.06] rounded-full overflow-hidden">
            <div className={`h-full ${rateBar} transition-all`} style={{ width: `${Math.min(rate, 100)}%` }} />
          </div>
        </SectionCard>

        <SectionCard title="Services les plus demandés" icon={BarChart3}>
          {statistics.popular_services?.length > 0 ? (
            <div className="space-y-2.5">
              {statistics.popular_services.map(([service, count]) => {
                const max = statistics.popular_services[0][1];
                const pct = (count / max) * 100;
                return (
                  <div key={service}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-white/80 truncate">{service}</span>
                      <span className="text-[#FF3B30] font-bold ml-2">{count}</span>
                    </div>
                    <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#FF3B30] to-[#D63025]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-white/40 text-center py-4 text-sm">Aucune donnée</p>
          )}
        </SectionCard>
      </div>

      {/* Today's appointments */}
      <SectionCard title="Rendez-vous aujourd'hui" icon={Clock} iconColor="text-emerald-400">
        {statistics.today_appointments?.length > 0 ? (
          <div className="space-y-2">
            {statistics.today_appointments.map((apt) => {
              const cfg = APPOINTMENT_STATUS[apt.status] || APPOINTMENT_STATUS.pending;
              return (
                <div key={apt.id} className="flex items-center gap-3 p-3 bg-[#1C1C1E] rounded-lg border border-white/[0.04]">
                  <div className="bg-emerald-500/15 text-emerald-400 px-3 py-1.5 rounded-md font-bold text-sm min-w-[60px] text-center">
                    {apt.time}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{apt.service_name}</p>
                    <p className="text-xs text-white/50 flex items-center gap-1.5 mt-0.5">
                      {apt.client_name}
                      <span className="text-white/20">•</span>
                      <Car size={11} className="text-[#FF3B30]" />
                      <span className="text-[#FF3B30] font-medium">{apt.client_plate}</span>
                    </p>
                  </div>
                  <StatusBadge config={cfg} />
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-white/40 text-center py-6 text-sm">Aucun rendez-vous aujourd'hui</p>
        )}
      </SectionCard>
    </div>
  );
};

export default StatisticsTab;
